// All LLM traffic (embeddings, chat, streaming chat) goes through the EPFL
// server's /llm/* proxy. The server holds the shared RCP application key and
// forwards each request to RCP after authenticating the caller via OIDC (or
// the legacy static API key for backwards compat). The browser never holds a
// personal RCP key — that mode was removed to simplify the security story:
// one path, one set of logs, one auth model.
//
// All consumers (rcpLLM, queryRewrite) go through this
// module so the routing rule lives in exactly one place.

import type { Config } from "../state/config";
import { authFetch } from "./authFetch";

export class RcpError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "RcpError";
  }
}

type Endpoint = "embeddings" | "chat" | "chat-stream";

// Kimi K2.5 / K2.6 sont thinking-on par défaut et ça peut prendre plusieurs
// minutes sur un long contexte → network error côté extension.
//
// RCP sert ces modèles via vLLM auto-hébergé, pas via le proxy Moonshot
// Platform : la syntaxe officielle `thinking: {type: "disabled"}` documentée
// sur docs.moonshot.ai est donc ignorée (pire : elle DOUBLE le reasoning).
// Le chat template Kimi sur vLLM attend la clé booléenne `thinking` dans
// `chat_template_kwargs`. C'est différent de Qwen3 qui utilise
// `enable_thinking`.
//
// Vérifié empiriquement le 2026-05-28 via server/scripts/probe_kimi_thinking.py :
// avec ce flag → 0 chars de reasoning, réponse en 1s. Sans → 226+ chars de
// reasoning, réponse en 2-4s.
//
// ⚠️ Volontairement limité à K2.5/K2.6. Re-probé sur K2.7-Code le 2026-07-30 :
// le flag n'y supprime pas le raisonnement, il le DÉPLACE de `reasoning_content`
// (que rcpLLM jette) vers `content` (qu'on affiche à l'utilisateur). Sur une
// question à réponse unique : 273 car. de divagation au lieu de 12, et c'est
// plus lent (16.7s vs 13.9s à 200k tokens). Sans le flag, K2.7-Code garde son
// raisonnement dans `reasoning_content` — exactement ce qu'on veut.
// Ne pas élargir cette regex aux prochains Kimi sans repasser le probe.
/** Le réglage de raisonnement à envoyer POUR CE MODÈLE-LÀ.
 *
 *  ⚠️ IL N'EXISTE AUCUN DRAPEAU UNIVERSEL, ET SE TROMPER NE LÈVE RIEN.
 *  Mesuré le 15.09.2026 sur un même prompt, sortie ≤ 800
 *  (DPO-Agent/docs/loadtest/resultats-2026-09-15.md, §4) :
 *
 *    modèle              chat_template_kwargs     reasoning_effort: low
 *    Qwen3.6-35B         ✅ 6 jetons              ❌ 367 jetons
 *    Qwen3.8-Flash-Next  ✅ 6 jetons              ❌ 154 jetons
 *    GLM-5.3-Flash       ❌ FUITE dans la réponse  ✅ 7 jetons
 *
 *  La ligne GLM est celle qui mord : `enable_thinking: false` n'y coupe pas la
 *  réflexion, il la fait sortir dans `content` À LA PLACE de la réponse. Un
 *  appelant qui attend du JSON reçoit de la prose anglaise et échoue sans
 *  erreur — c'est exactement la panne que ce helper existe pour empêcher.
 *
 *  Deux niveaux, parce que le compromis n'est pas le même selon la tâche :
 *
 *  - "coupe" : passes MÉCANIQUES (réécriture, tri, choix d'outil). Aucune
 *    d'elles n'a besoin de réfléchir, et un budget serré part entier en
 *    réflexion avant la première ligne utile.
 *  - "bas" : RÉPONSE FINALE, Réflexion coupée (défaut). Sur la lecture de
 *    document, `low` ne coûte rien (2,99 dans les deux modes, 7 fois moins de
 *    jetons) ; sur la recherche il coûte 0,19 point en apparié. Compromis
 *    assumé : l'interrupteur Réflexion existe pour qui veut mieux.
 *
 *  Un modèle absent de la table ne reçoit RIEN plutôt qu'un drapeau au hasard :
 *  garder sa réflexion coûte des jetons, la casser coûte la réponse. */
export function reasoningParams(
  model: string,
  niveau: "coupe" | "bas" | "libre",
): Record<string, unknown> {
  // "libre" = on n'envoie rien, le modèle réfléchit à sa guise. C'est la
  // Réflexion activée : +0,19 point en recherche, rien de plus en lecture de document, pour
  // sept fois plus de jetons.
  if (niveau === "libre") return {};
  // GLM-5.x : `reasoning_effort` est le seul levier qui marche, aux deux
  // niveaux — "coupe" descend déjà à ~7 jetons, il n'y a pas plus bas.
  if (/glm-5/i.test(model)) return { reasoning_effort: "low" };
  if (niveau === "bas") return {};
  // Kimi K2.5 / K2.6 lisent `thinking` ; les Qwen3 lisent `enable_thinking`.
  if (/kimi-k2\.[56]/i.test(model)) {
    return { chat_template_kwargs: { thinking: false } };
  }
  // ⚠️ K2.7 est exclu : re-probé le 30.07.2026, le drapeau n'y supprime pas la
  // réflexion, il la DÉPLACE de `reasoning_content` (que rcpLLM jette) vers
  // `content` (qu'on affiche). Même défaut que GLM, découvert un mois plus tôt.
  if (/kimi-k2/i.test(model)) return {};
  if (/qwen3/i.test(model)) {
    return { chat_template_kwargs: { enable_thinking: false } };
  }
  return {};
}

function withModelTweaks(body: Record<string, unknown>): Record<string, unknown> {
  const model = typeof body.model === "string" ? body.model : "";
  if (/kimi-k2\.[56]/i.test(model)) {
    const existing = (body.chat_template_kwargs as Record<string, unknown>) ?? {};
    return {
      ...body,
      chat_template_kwargs: { ...existing, thinking: false },
    };
  }
  return body;
}

function endpointUrl(kind: Endpoint, cfg: Config): string {
  const path =
    kind === "embeddings"
      ? "/llm/embeddings"
      : kind === "chat"
        ? "/llm/chat"
        : "/llm/chat/stream";
  return cfg.serverUrl.replace(/\/$/, "") + path;
}

async function postJson(
  kind: "embeddings" | "chat",
  body: unknown,
  cfg: Config,
  signal?: AbortSignal,
): Promise<unknown> {
  const res = await authFetch(endpointUrl(kind, cfg), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new RcpError(res.status, `RCP ${res.status}: ${detail.slice(0, 300)}`);
  }
  return res.json();
}

export async function embedRequest(
  body: { model: string; input: string[] },
  cfg: Config,
  signal?: AbortSignal,
): Promise<{ data: Array<{ embedding: number[] }>; usage?: { prompt_tokens?: number } }> {
  return (await postJson("embeddings", body, cfg, signal)) as {
    data: Array<{ embedding: number[] }>;
    usage?: { prompt_tokens?: number };
  };
}

/** A function tool-call requested by the model in a non-streaming response. */
export interface ToolCallResponse {
  id: string;
  type?: string;
  function: { name: string; arguments: string };
}

export async function chatRequest(
  body: Record<string, unknown>,
  cfg: Config,
  signal?: AbortSignal,
): Promise<{
  choices?: Array<{
    message?: { content?: string; tool_calls?: ToolCallResponse[] };
    finish_reason?: string;
  }>;
  usage?: { prompt_tokens?: number; completion_tokens?: number };
}> {
  // Force stream=false — the proxy returns plain JSON.
  const payload = withModelTweaks({ ...body, stream: false });
  return (await postJson("chat", payload, cfg, signal)) as {
    choices?: Array<{
      message?: { content?: string; tool_calls?: ToolCallResponse[] };
      finish_reason?: string;
    }>;
    usage?: { prompt_tokens?: number; completion_tokens?: number };
  };
}

export async function chatStream(
  body: Record<string, unknown>,
  cfg: Config,
  signal?: AbortSignal,
): Promise<Response> {
  const payload = withModelTweaks({ ...body, stream: true });
  const res = await authFetch(endpointUrl("chat-stream", cfg), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
  });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    throw new RcpError(res.status, `RCP ${res.status}: ${text.slice(0, 300)}`);
  }
  return res;
}
